'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import styles from './ExplainerToc.module.css';

export interface TocItem {
  id: string;
  label: string;
  level: number; // 2 = h2 (parent), 3 = h3 (nested child)
}

interface Props {
  toc: TocItem[];
  /* Inline = rendered in the content column (tablet / mobile) with top and
     bottom rules, not sticky. Default is the sticky rail. */
  inline?: boolean;
}

interface Section {
  parent: TocItem | null;
  children: TocItem[];
}

/* Group a flat heading list into parent (h2) sections, each owning the run of
   h3s that follow it — so the nested guide line can wrap them. */
const groupToc = (toc: TocItem[]): Section[] => {
  const sections: Section[] = [];
  toc.forEach((item) => {
    if (item.level <= 2) {
      sections.push({ parent: item, children: [] });
    } else {
      if (sections.length === 0) sections.push({ parent: null, children: [] });
      sections[sections.length - 1].children.push(item);
    }
  });
  return sections;
};

/* Table of contents — nested, mirroring the docs reference:
 *
 *   TABLE OF CONTENTS
 *   Access personal settings        ← parent (h2), active = ink
 *   │  Update web search prefs      ← child (h3) under a guide line
 *   │  Share downvoted messages
 *   Access workspace settings
 *
 * Highlights the heading in view by position; click smooth-scrolls. Sticky
 * rail on desktop; inline list on tablet/mobile. */
const ExplainerToc = ({ toc, inline = false }: Props) => {
  const t = useTranslations('science');
  const [activeId, setActiveId] = useState<string | null>(toc[0]?.id ?? null);
  /* When the reader clicks a TOC entry we set it active immediately and lock
     out the scroll-spy until they scroll again. Without this the highlight
     snaps to a neighbour: a clicked entry near the bottom of the page cannot
     reach the activation line (the page is already at max scroll), so the spy
     would mark whatever sits on the line. The lock releases on the next
     genuine user scroll (wheel / touch / scroll keys) — a programmatic
     smooth-scroll fires none of those. */
  const clickLockUntil = useRef(0);

  useEffect(() => {
    const release = () => {
      clickLockUntil.current = 0;
    };
    const onKey = (e: KeyboardEvent) => {
      if (
        [
          'ArrowDown',
          'ArrowUp',
          'PageDown',
          'PageUp',
          'Home',
          'End',
          ' ',
        ].includes(e.key)
      ) {
        release();
      }
    };
    window.addEventListener('wheel', release, { passive: true });
    window.addEventListener('touchmove', release, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('wheel', release);
      window.removeEventListener('touchmove', release);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  /* Scroll-spy by position: the active heading is the last one whose top has
     passed the activation line (~120px, matching scroll-margin-top). Unlike an
     IntersectionObserver band, this never gets stuck when a section is taller
     than the viewport or when scrolling upward — there is always a
     well-defined current heading. */
  useEffect(() => {
    if (toc.length === 0) return undefined;
    const LINE = 120;
    let raf = 0;
    const compute = () => {
      raf = 0;
      if (Date.now() < clickLockUntil.current) return;
      const ids = toc.map((item) => item.id);
      let current: string | null = ids[0] ?? null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - LINE <= 1) current = id;
      }
      /* At the very bottom of the page force the last heading active, so a
         tall final section does not keep highlighting an earlier one. */
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) current = ids[ids.length - 1] ?? current;
      setActiveId((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [toc]);

  if (toc.length === 0) return null;

  const sections = groupToc(toc);

  const go = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    /* If the anchor is a FAQ question, open its accordion first. */
    const details = target.closest('details');
    if (details && !details.open) details.open = true;
    /* Highlight the clicked entry right away and hold it (until the reader
       scrolls) so it stays selected even when it lands outside the spy band.
       Auto-releases after ~800ms. */
    setActiveId(id);
    clickLockUntil.current = Date.now() + 800;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <nav
      className={styles.wrap}
      aria-label={t('science.explainers.nav.toc')}
      data-inline={inline ? 'true' : undefined}
    >
      <div className={styles.label}>{t('science.explainers.nav.toc')}</div>
      <div className={styles.list}>
        {sections.map((sec, i) => (
          <Fragment key={sec.parent?.id ?? `sec-${i}`}>
            {sec.parent && (
              <a
                className={styles.parent}
                href={`#${sec.parent.id}`}
                data-active={sec.parent.id === activeId ? 'true' : undefined}
                onClick={(e) => go(e, sec.parent!.id)}
              >
                {sec.parent.label}
              </a>
            )}
            {sec.children.length > 0 && (
              <ul className={styles.leaves}>
                {sec.children.map((c) => (
                  <li key={c.id}>
                    <a
                      className={styles.child}
                      href={`#${c.id}`}
                      data-active={c.id === activeId ? 'true' : undefined}
                      onClick={(e) => go(e, c.id)}
                    >
                      {c.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Fragment>
        ))}
      </div>
    </nav>
  );
};

export default ExplainerToc;
